import { IMAGE_CONFIG } from './constants'

// Generate unique ID

// Format date for display
export const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

// Format date and time for display
export const formatDateTime = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Format date for filenames
export const formatDateForFilename = (dateString) => {
  const date = new Date(dateString)
  return date.toISOString().split('T')[0]
}

// Compress and resize image
export const compressImage = (file, maxWidth = IMAGE_CONFIG.maxWidth, maxHeight = IMAGE_CONFIG.maxHeight, quality = IMAGE_CONFIG.quality) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img

        // Calculate new dimensions
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to compress image'))
            }
          },
          'image/jpeg',
          quality
        )
      }
      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = e.target.result
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

// Create thumbnail from image blob
export const createThumbnail = (blob, size = IMAGE_CONFIG.thumbnailSize) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img

        // Calculate thumbnail dimensions (square crop from center)
        const minDim = Math.min(width, height)
        const startX = (width - minDim) / 2
        const startY = (height - minDim) / 2

        canvas.width = size
        canvas.height = size

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, startX, startY, minDim, minDim, 0, 0, size, size)

        resolve(canvas.toDataURL('image/jpeg', 0.7))
      }
      img.onerror = () => reject(new Error('Failed to load image for thumbnail'))
      img.src = e.target.result
    }
    reader.onerror = () => reject(new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

// Convert blob to base64
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(new Error('Failed to convert blob to base64'))
    reader.readAsDataURL(blob)
  })
}

// Convert base64 to blob
export const base64ToBlob = (base64, contentType = 'image/jpeg') => {
  const byteCharacters = atob(base64.split(',')[1])
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512)
    const byteNumbers = new Array(slice.length)

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentType })
}

// Get file size in human readable format
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Sanitize filename
export const sanitizeFilename = (filename) => {
  return filename
    .replace(/[^a-z0-9]/gi, '_')
    .replace(/_+/g, '_')
    .toLowerCase()
}

// Group photos by category
export const groupPhotosByCategory = (photos, categories) => {
  const grouped = {}

  categories.forEach(category => {
    grouped[category.code] = {
      category,
      photos: photos.filter(photo => photo.categoryCode === category.code)
    }
  })

  return grouped
}

// Calculate survey progress
export const calculateSurveyProgress = (photos, categories) => {
  if (categories.length === 0) return 0

  const categoriesWithPhotos = new Set(photos.map(p => p.categoryCode))
  return Math.round((categoriesWithPhotos.size / categories.length) * 100)
}

// Debounce function
export const debounce = (func, wait) => {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

// Check if device is online
export const isOnline = () => navigator.onLine

// Check if device supports camera
export const supportsCamera = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
}

// Check if running on iPad/tablet
export const isTablet = () => {
  const userAgent = navigator.userAgent.toLowerCase()
  return /ipad|tablet|playbook|silk/i.test(userAgent) ||
    (navigator.maxTouchPoints > 1 && /macintosh/i.test(userAgent))
}

// Truncate text
export const truncateText = (text, maxLength = 100) => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}

// Generate survey report filename
export const generateReportFilename = (survey) => {
  const date = formatDateForFilename(survey.createdAt)
  const name = sanitizeFilename(survey.propertyName || 'survey')
  return `${name}_survey_${date}.docx`
}

// Generate photos zip filename
export const generatePhotosZipFilename = (survey) => {
  const date = formatDateForFilename(survey.createdAt)
  const name = sanitizeFilename(survey.propertyName || 'survey')
  return `${name}_photos_${date}.zip`
}
